import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LocalAuthorizationList, OCPPChargepoint } from '../../../api/chargepoints-api.service';
import { ChargepointsOcppApiService } from '../../../api/chargepoints-ocpp-api.service';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-chargepoint-local-authorization-list-ocpp',
  templateUrl: './chargepoint-local-authorization-list-ocpp.component.html',
  styleUrls: ['./chargepoint-local-authorization-list-ocpp.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    MatSnackBarModule,
    MatTooltipModule,
  ],
})
export class ChargepointLocalAuthorizationListOcppComponent {

  @Input()
  chargepoint!: OCPPChargepoint;

  @Output()
  refresh = new EventEmitter<void>();

  public newIdentifier = '';
  public pending = false;

  constructor(
    private chargepointsOcppApiService: ChargepointsOcppApiService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
  ) {}

  public get localAuthorizationList(): LocalAuthorizationList | undefined {
    return this.chargepoint?.localAuthorizationList;
  }

  public requestToggleEnabled(): void {
    if (this.pending || !this.localAuthorizationList) {
      return;
    }

    const nextEnabled = !this.localAuthorizationList.enabled;
    this.confirmAction(
      {
        title: nextEnabled ? 'Enable local authorization list' : 'Disable local authorization list',
        content: nextEnabled
          ? 'Are you sure you want to enable the local authorization list on this chargepoint?'
          : 'Are you sure you want to disable the local authorization list on this chargepoint?',
        confirmLabel: nextEnabled ? 'Enable' : 'Disable',
      },
      () => this.setEnabled(nextEnabled),
    );
  }

  public requestSynchronization(): void {
    if (this.pending || !this.localAuthorizationList?.enabled || this.localAuthorizationList?.synchronizationDone) {
      return;
    }

    this.confirmAction(
      {
        title: 'Synchronize local authorization list',
        content: 'Are you sure you want to synchronize the local authorization list with this chargepoint?',
        confirmLabel: 'Synchronize',
      },
      () => this.synchronize(),
    );
  }

  public addIdentifier(): void {
    const identifier = this.newIdentifier.trim();
    if (!identifier || this.pending) {
      return;
    }

    this.pending = true;
    this.chargepointsOcppApiService
      .addLocalAuthorizationListIdentifier(this.chargepoint.id, identifier)
      .subscribe({
        next: () => {
          this.newIdentifier = '';
          this.snackBar.open('Identifier added', 'Close', { duration: 3000 });
          this.refresh.emit();
        },
        error: () => {
          this.snackBar.open('Failed to add identifier', 'Close', { duration: 3000 });
        },
        complete: () => {
          this.pending = false;
        },
      });
  }

  public removeIdentifier(identifier: string): void {
    if (this.pending) {
      return;
    }

    this.pending = true;
    this.chargepointsOcppApiService
      .removeLocalAuthorizationListIdentifier(this.chargepoint.id, identifier)
      .subscribe({
        next: () => {
          this.snackBar.open('Identifier removed', 'Close', { duration: 3000 });
          this.refresh.emit();
        },
        error: () => {
          this.snackBar.open('Failed to remove identifier', 'Close', { duration: 3000 });
        },
        complete: () => {
          this.pending = false;
        },
      });
  }

  private applyLocalAuthorizationListUpdate(updatedList: LocalAuthorizationList): void {
    if (this.chargepoint.localAuthorizationList) {
      Object.assign(this.chargepoint.localAuthorizationList, updatedList);
    } else {
      this.chargepoint.localAuthorizationList = updatedList;
    }
  }

  private setEnabled(enabled: boolean): void {
    this.pending = true;
    this.chargepointsOcppApiService
      .setLocalAuthorizationListEnabled(this.chargepoint.id, enabled)
      .subscribe({
        next: (updatedList) => {
          this.applyLocalAuthorizationListUpdate(updatedList);
          this.snackBar.open(
            enabled ? 'Local authorization list enabled' : 'Local authorization list disabled',
            'Close',
            { duration: 3000 },
          );
          this.refresh.emit();
        },
        error: () => {
          this.snackBar.open('Failed to update local authorization list status', 'Close', { duration: 3000 });
        },
        complete: () => {
          this.pending = false;
        },
      });
  }

  private synchronize(): void {
    this.pending = true;
    this.chargepointsOcppApiService
      .synchronizeLocalAuthorizationList(this.chargepoint.id)
      .subscribe({
        next: (updatedList) => {
          this.applyLocalAuthorizationListUpdate(updatedList);
          this.snackBar.open('Local authorization list synchronized', 'Close', { duration: 3000 });
          this.refresh.emit();
        },
        error: () => {
          this.snackBar.open('Failed to synchronize local authorization list', 'Close', { duration: 3000 });
        },
        complete: () => {
          this.pending = false;
        },
      });
  }

  private confirmAction(
    dialogProps: { title: string; content: string; confirmLabel?: string; cancelLabel?: string },
    action: () => void,
  ): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogProps,
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        action();
      }
    });
  }
}
